/***************************************************************************************
 * FileName      : BubbleEffectService.ts
 * Description   : 3D 공간에 거품 부글부글 끓는 파티클 애니메이션을 배치/제어
 *                 - 커스텀 mp.bubbleEmitter Scene Component 등록
 *                 - 각 emitter 는 source point 에서 위로 상승하는 작은 구(sphere) 풀 관리
 *                 - onTick 마다 age 증가 → 위치/스케일/투명도 갱신 → 수명 다하면 재스폰
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.05.15
 * Modified Date : 2026.07.02 matterport_mvp1/14-bubble-effect 에서 포팅 — 시뮬레이션 작동(RUN=ON)
 *                 장비 위 거품 효과 표시용. 매 tick addEmitter 호출 시 중복 노드 생성 방지 가드 추가.
 * Reference     : matterport_mvp1/src/14-bubble-effect/services/BubbleEffectService,
 *                 DeviceHighlightService (커스텀 Scene Component 등록 패턴)
 ***************************************************************************************/

import { SceneComponent } from '@mp/common';

// ============================================================================
// 타입 정의
// ============================================================================

export interface BubbleEmitterConfig {
  /** emitter 식별자 (clear/update 용) */
  id: string;
  /** UI 표시용 라벨 (선택) */
  label?: string;
  /** 거품이 솟아나는 시작점 (3D 월드 좌표) */
  position: { x: number; y: number; z: number };
  /** 거품 평균 반지름 (미터). 기본 0.025 */
  radius?: number;
  /** 반지름 랜덤 가산치 (±). 기본 0.012 */
  radiusVariance?: number;
  /** 초당 거품 생성 개수. 기본 25 */
  spawnRate?: number;
  /** 상승 속도 (m/s). 기본 0.35 */
  riseSpeed?: number;
  /** 거품 수명 (초). 기본 1.6 */
  lifetime?: number;
  /** 거품 색상 (16진 RGB). 기본 0xddf2ff (옅은 하늘색) */
  color?: number;
  /** 가로 분산 반지름 (미터). 시작점 주위로 흩어져 스폰. 기본 0.06 */
  spread?: number;
  /** 동시 최대 거품 개수 (풀 크기). 기본 80 */
  maxBubbles?: number;
}

// ============================================================================
// 기본값
// ============================================================================

const DEFAULTS = {
  radius: 0.025,
  radiusVariance: 0.012,
  spawnRate: 25,
  riseSpeed: 0.35,
  lifetime: 1.6,
  color: 0xddf2ff,
  spread: 0.06,
  maxBubbles: 80,
};

// ============================================================================
// 커스텀 Scene Component (mp.bubbleEmitter)
// ============================================================================

const EMITTER_COMPONENT_TYPE = 'mp.bubbleEmitter';

interface Particle {
  mesh: any;
  age: number;       // 현재 나이 (초)
  lifetime: number;  // 이 입자의 수명 (초)
  baseRadius: number;
  velocity: { x: number; y: number; z: number };
}

interface EmitterInputs {
  radius: number;
  radiusVariance: number;
  spawnRate: number;
  riseSpeed: number;
  lifetime: number;
  color: number;
  spread: number;
  maxBubbles: number;
}

class BubbleEmitterComponent extends SceneComponent {
  inputs: EmitterInputs = { ...DEFAULTS };

  private root: any = null;
  private particles: Particle[] = [];
  private material: any = null;
  private geometry: any = null;
  private spawnAccumulator = 0;
  // 자체 clock — onTick(deltaMs) 의 의미가 환경에 따라 다를 수 있어 performance.now 기반 dt 사용
  private lastTickMs: number = 0;

  onInit() {
    const THREE = this.context.three;
    this.root = new THREE.Object3D();
    // matrixAutoUpdate false 로 잠그면 외부 매트릭스 갱신 방지 (위치 고정 보장)
    this.root.matrixAutoUpdate = true;
    this.outputs.objectRoot = this.root;
    this.outputs.collider = this.root;

    // 거품 한 종류로 공유 (geometry + material 재사용)
    this.geometry = new THREE.SphereGeometry(1, 12, 10);
    this.material = new THREE.MeshBasicMaterial({
      color: this.inputs.color,
      transparent: true,
      opacity: 0.7,
      depthWrite: false,
      // depthTest: true (기본) — 정상적인 깊이 처리. 벽 안쪽이면 가려짐.
    });

    // 풀 미리 생성 (visible=false 로 대기, 스폰 시 활성)
    for (let i = 0; i < this.inputs.maxBubbles; i++) {
      const mesh = new THREE.Mesh(this.geometry, this.material);
      mesh.visible = false;
      this.root.add(mesh);
      this.particles.push({
        mesh,
        age: 0,
        lifetime: 0,
        baseRadius: 0,
        velocity: { x: 0, y: 0, z: 0 },
      });
    }

    // 검증 로그: 첫 프레임에 root 의 world position 1회 출력
    setTimeout(() => {
      try {
        const wp = new THREE.Vector3();
        this.root.getWorldPosition(wp);
        console.log(`[BubbleEmitter] world position fixed at: (${wp.x.toFixed(3)}, ${wp.y.toFixed(3)}, ${wp.z.toFixed(3)})`);
      } catch (_) {}
    }, 500);
  }

  onInputsUpdated(_prev: EmitterInputs) {
    // 색상만 즉시 반영 (material 단일 인스턴스 공유). 나머지 (radius/spawnRate/riseSpeed/lifetime/spread) 는
    // onTick 에서 this.inputs.X 를 매 프레임 직접 참조하므로 별도 처리 불필요.
    // maxBubbles 는 풀 크기 고정 — 변경 시 재생성 필요 (현재 미지원).
    if (this.material && typeof this.inputs.color === 'number') {
      this.material.color.setHex(this.inputs.color);
    }
  }

  onTick(_deltaMs: number) {
    // 자체 clock 으로 dt 산출 (외부 deltaMs 는 환경마다 의미가 달라 신뢰 불가)
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (this.lastTickMs === 0) {
      this.lastTickMs = now;
      return;
    }
    const dt = Math.min(0.1, (now - this.lastTickMs) / 1000); // 최대 100ms 클램프 (탭 비활성 후 점프 방지)
    this.lastTickMs = now;
    if (dt <= 0) return;

    // 1) 스폰: spawnRate 만큼 누적해서 정수 개수로 스폰
    this.spawnAccumulator += dt * this.inputs.spawnRate;
    while (this.spawnAccumulator >= 1) {
      this.spawnAccumulator -= 1;
      this.spawnOne();
    }

    // 2) 업데이트: 모든 활성 입자 위치/스케일/투명도 갱신 (this.root 의 LOCAL 좌표)
    for (const p of this.particles) {
      if (!p.mesh.visible) continue;
      p.age += dt;

      if (p.age >= p.lifetime) {
        p.mesh.visible = false;
        continue;
      }

      // velocity 누적 (LOCAL 좌표 — root 는 node 의 world 위치에 고정되어 있음)
      p.mesh.position.x += p.velocity.x * dt;
      p.mesh.position.y += p.velocity.y * dt;
      p.mesh.position.z += p.velocity.z * dt;

      // 좌우 미세 흔들림 (사인파)
      const swayPhase = p.age * 4 + p.baseRadius * 100;
      p.mesh.position.x += Math.sin(swayPhase) * 0.0008;
      p.mesh.position.z += Math.cos(swayPhase * 0.8) * 0.0008;

      // 크기: 작게 시작 → 살짝 커짐
      const lifeT = p.age / p.lifetime;
      const sizeFactor = 0.6 + lifeT * 0.6;
      const scale = p.baseRadius * sizeFactor;
      p.mesh.scale.set(scale, scale, scale);
    }
  }

  private spawnOne() {
    // 비활성 입자 찾기
    const idle = this.particles.find(p => !p.mesh.visible);
    if (!idle) return; // 풀 가득 차면 스킵

    const r = this.inputs.radius + (Math.random() * 2 - 1) * this.inputs.radiusVariance;
    const ang = Math.random() * Math.PI * 2;
    const dist = Math.random() * this.inputs.spread;
    const offsetX = Math.cos(ang) * dist;
    const offsetZ = Math.sin(ang) * dist;

    idle.age = 0;
    idle.lifetime = this.inputs.lifetime * (0.7 + Math.random() * 0.6); // ±30% 변동
    idle.baseRadius = Math.max(0.005, r);
    idle.velocity = {
      x: 0,
      y: this.inputs.riseSpeed * (0.8 + Math.random() * 0.4),
      z: 0,
    };

    idle.mesh.position.set(offsetX, 0, offsetZ);
    idle.mesh.scale.set(idle.baseRadius * 0.6, idle.baseRadius * 0.6, idle.baseRadius * 0.6);
    idle.mesh.visible = true;
  }

  onDestroy() {
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
    this.particles = [];
  }
}

const bubbleEmitterFactory = () => new BubbleEmitterComponent();

// ============================================================================
// 서비스 본체
// ============================================================================

interface ActiveEmitter {
  node: any;
  component: any;
  config: BubbleEmitterConfig;
}

export class BubbleEffectService {
  private static instance: BubbleEffectService;
  private sdk: any = null;
  private emitters: Map<string, ActiveEmitter> = new Map();
  private componentRegistered = false;
  // 2026.07.02 : 박은정 : 생성 진행 중인 emitter id (매 tick addEmitter 재호출 시 중복 노드 생성 방지)
  private creating: Set<string> = new Set();

  static getInstance(): BubbleEffectService {
    if (!BubbleEffectService.instance) {
      BubbleEffectService.instance = new BubbleEffectService();
    }
    return BubbleEffectService.instance;
  }

  /**
   * SDK 핸들 주입 + 커스텀 컴포넌트 등록. componentDidMount 시점에 1회 호출.
   */
  async initialize(sdk: any): Promise<void> {
    this.sdk = sdk;

    if (!this.componentRegistered) {
      await sdk.Scene.register(EMITTER_COMPONENT_TYPE, bubbleEmitterFactory);
      this.componentRegistered = true;
      console.log('[BubbleEffectService] mp.bubbleEmitter 컴포넌트 등록 완료');
    }
  }

  /**
   * 거품 emitter 추가. 같은 id 가 이미 있으면 in-place 로 inputs/위치 만 갱신
   * (노드 재생성하지 않으므로 진행 중인 거품들이 끊기지 않음).
   */
  async addEmitter(config: BubbleEmitterConfig): Promise<void> {
    if (!this.sdk) {
      console.warn('[BubbleEffectService] initialize() 미호출 — emitter 추가 불가');
      return;
    }

    const existing = this.emitters.get(config.id);
    if (existing) {
      this.applyInputsInPlace(existing.component, config);
      const pos = config.position;
      existing.node.position.set(pos.x, pos.y, pos.z);
      existing.config = config;
      return;
    }

    // 2026.07.02 : 박은정 : 생성이 진행 중이면 스킵 — 매 tick 재호출 시 await 사이에 중복 노드 생성 방지
    if (this.creating.has(config.id)) return;
    this.creating.add(config.id);

    try {
      const node = await this.sdk.Scene.createNode();
      node.position.set(config.position.x, config.position.y, config.position.z);

      const inputs = this.buildInputs(config);
      const component = node.addComponent(EMITTER_COMPONENT_TYPE, inputs);
      node.start();

      this.emitters.set(config.id, { node, component, config });
      console.log(`[BubbleEffectService] emitter '${config.id}' 생성 — pos:`, config.position, 'inputs:', inputs);
    } catch (error) {
      console.error('[BubbleEffectService] emitter 생성 실패:', error);
    } finally {
      this.creating.delete(config.id);
    }
  }

  /**
   * 활성 emitter 전체에 부분 inputs 갱신. 위치는 유지.
   * UI 슬라이더 변경 시 매번 호출.
   */
  updateAllInputs(partial: Partial<Omit<BubbleEmitterConfig, 'id' | 'position'>>): void {
    for (const e of this.emitters.values()) {
      const merged = { ...e.config, ...partial };
      this.applyInputsInPlace(e.component, merged);
      e.config = merged;
    }
  }

  private buildInputs(config: BubbleEmitterConfig): EmitterInputs {
    return {
      radius: config.radius ?? DEFAULTS.radius,
      radiusVariance: config.radiusVariance ?? DEFAULTS.radiusVariance,
      spawnRate: config.spawnRate ?? DEFAULTS.spawnRate,
      riseSpeed: config.riseSpeed ?? DEFAULTS.riseSpeed,
      lifetime: config.lifetime ?? DEFAULTS.lifetime,
      color: config.color ?? DEFAULTS.color,
      spread: config.spread ?? DEFAULTS.spread,
      maxBubbles: config.maxBubbles ?? DEFAULTS.maxBubbles,
    };
  }

  /**
   * 컴포넌트 inputs 를 in-place 갱신 → SDK 가 onInputsUpdated 자동 호출.
   * radius/spawnRate/riseSpeed/lifetime/spread 는 onTick 에서 매 프레임 읽으므로 즉시 반영됨.
   * color 는 onInputsUpdated 에서 material.color.setHex 로 반영.
   * maxBubbles 는 풀 크기라 변경 무시 (재생성 필요).
   */
  private applyInputsInPlace(component: any, config: BubbleEmitterConfig) {
    const inputs = this.buildInputs(config);
    component.inputs.radius = inputs.radius;
    component.inputs.radiusVariance = inputs.radiusVariance;
    component.inputs.spawnRate = inputs.spawnRate;
    component.inputs.riseSpeed = inputs.riseSpeed;
    component.inputs.lifetime = inputs.lifetime;
    component.inputs.color = inputs.color;
    component.inputs.spread = inputs.spread;
    // maxBubbles 는 변경하지 않음 (풀 크기는 고정)
  }

  /**
   * 특정 emitter 제거.
   */
  removeEmitter(id: string): void {
    const e = this.emitters.get(id);
    if (!e) return;
    try {
      e.node.stop();
    } catch (_) { /* noop */ }
    this.emitters.delete(id);
  }

  /**
   * 모든 emitter 제거.
   */
  clear(): void {
    for (const id of Array.from(this.emitters.keys())) {
      this.removeEmitter(id);
    }
  }

  /**
   * 현재 활성 emitter id 목록.
   */
  listEmitterIds(): string[] {
    return Array.from(this.emitters.keys());
  }

  /**
   * 특정 emitter 의 현재 설정 (저장용 / UI 표시용).
   */
  getConfig(id: string): BubbleEmitterConfig | null {
    return this.emitters.get(id)?.config ?? null;
  }

  /**
   * 모든 emitter 의 현재 설정 배열 (저장용).
   */
  getAllConfigs(): BubbleEmitterConfig[] {
    return Array.from(this.emitters.values()).map(e => ({ ...e.config }));
  }

  /**
   * 특정 emitter 의 위치만 즉시 갱신 (위치 조정 UI 용).
   */
  updatePosition(id: string, position: { x: number; y: number; z: number }): void {
    const e = this.emitters.get(id);
    if (!e) return;
    e.node.position.set(position.x, position.y, position.z);
    e.config = { ...e.config, position };
  }

  /**
   * 특정 emitter 의 label(표시 명칭) 만 갱신. 3D 동작에는 영향 없음, 저장 JSON 에만 반영.
   */
  updateLabel(id: string, label: string): void {
    const e = this.emitters.get(id);
    if (!e) return;
    e.config = { ...e.config, label };
  }

  /**
   * 선택된 emitter 를 동일 설정으로 복제. 새 id 는 자동 생성 ('-copy', '-copy-2', ...).
   * 위치는 source 에서 X 축으로 약간 오프셋 (겹쳐 보이지 않게).
   * 반환값: 생성된 새 emitter id (실패 시 null).
   */
  async duplicateEmitter(sourceId: string, xOffset: number = 0.15): Promise<string | null> {
    const e = this.emitters.get(sourceId);
    if (!e) return null;

    // 새 id 생성: '-copy', '-copy-2', ... 순차 시도
    let newId = `${sourceId}-copy`;
    let n = 2;
    while (this.emitters.has(newId)) {
      newId = `${sourceId}-copy-${n}`;
      n += 1;
    }

    const newConfig: BubbleEmitterConfig = {
      ...e.config,
      id: newId,
      label: e.config.label ? `${e.config.label} 사본` : undefined,
      position: {
        x: e.config.position.x + xOffset,
        y: e.config.position.y,
        z: e.config.position.z,
      },
    };

    await this.addEmitter(newConfig);
    return newId;
  }

  /**
   * 특정 emitter 의 id 변경. Map 키 + config.id 갱신, 노드 자체는 재생성하지 않음.
   * 새 id 가 이미 존재하면 무시.
   */
  renameId(oldId: string, newId: string): boolean {
    if (oldId === newId) return true;
    if (!this.emitters.has(oldId)) return false;
    if (this.emitters.has(newId)) {
      console.warn(`[BubbleEffectService] '${newId}' 가 이미 사용 중 — 변경 취소`);
      return false;
    }
    const e = this.emitters.get(oldId)!;
    e.config = { ...e.config, id: newId };
    this.emitters.delete(oldId);
    this.emitters.set(newId, e);
    return true;
  }
}
